import { useTheme } from "next-themes";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Tooltip, Whisper } from "rsuite";
import { Quadrant } from "./enums/quadrant";
import { Ring } from "./enums/ring";
import { Group } from "./interfaces/group";
import { Information } from "./interfaces/Information";
import { Point } from "./interfaces/point";

interface PointProps {
  points: Point[];
  onClick: (data: Point) => void;
  quadrant: Quadrant;
}

interface Props {
  quadrants: string[];
  rings: string[];
  data: any;
  width: string;
  onClick: (element: Point) => void;
}

const DrawPoints = ({ points, onClick, quadrant }: PointProps) => {
  let quadrantStyle = "circle-element ";

  if (quadrant === Quadrant.Zero) quadrantStyle += "ce-q0";
  else if (quadrant === Quadrant.One) quadrantStyle += "ce-q1";
  else if (quadrant === Quadrant.Two) quadrantStyle += "ce-q2";
  else if (quadrant === Quadrant.Three) quadrantStyle += "ce-q3";

  return (
    <>
      {points.map((point) => (
        <>
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>{point.info[0].description}</Tooltip>}
          >
            <div
              onClick={() => onClick(point)}
              key={`${point.y}${point.x}`}
              className={quadrantStyle}
              style={{ bottom: `${point.y}%`, left: `${point.x}%` }}
            >
              {point.quantity}
            </div>
          </Whisper>
        </>
      ))}
    </>
  );
};

const Radar = (props: Props) => {
  const { rings, data, width, quadrants, onClick } = props;
  const { t } = useTranslation("common");
  const [, setValidQuadrants] = useState<{ index: Quadrant; name: string }[]>();
  const [, setValidRings] = useState<{ index: Ring; name: string }[]>();
  const { theme } = useTheme();

  // Optimal resolution width 1200px
  // Inverse proportionality resolution - correction
  const correction = 3;
  const [positionsQZero, setPositionsQZero] = useState<Point[]>([]);
  const [positionsQOne, setPositionsQOne] = useState<Point[]>([]);
  const [positionsQTwo, setPositionsQTwo] = useState<Point[]>([]);
  const [positionsQThree, setPositionsQThree] = useState<Point[]>([]);

  const squareRoot = (a: number): number =>
    Math.round(Math.sqrt(a) * 100) / 100;

  const randomPosition = (
    quadrant: Quadrant,
    ring: Ring,
    quantity: number,
    info: Information[]
  ): Point => {
    let maxRadius = 0;
    let minRadius = 0;
    let radius = 0;
    let maxX = 0;
    let minX = 0;
    let x = 0;
    let y = 0;

    if (ring === Ring.Zero) {
      maxRadius = 38.8; // 40.8 -2
      minRadius = 6; // 0    +6
    }

    if (ring === Ring.One) {
      maxRadius = 66; // 68   -2
      minRadius = 44.8; // 40.8 +4
    }

    if (ring === Ring.Two) {
      maxRadius = 83; // 85 -2
      minRadius = 72; // 68 +4
    }

    if (ring === Ring.Three) {
      maxRadius = 98; // 100 -2
      minRadius = 89; // 85  +3
    }

    radius = Math.random() * (maxRadius - minRadius) + minRadius;
    maxX = radius - 0.7;
    minX = 3;

    if (quadrant === Quadrant.Zero) {
      x = Math.random() * (maxX - minX) + minX;
      y = squareRoot(radius * radius - x * x);
    }

    if (quadrant === Quadrant.One) {
      x = Math.random() * (maxX - minX) + minX;
      y = squareRoot(radius * radius - x * x);
      y = 101 - y;
    }

    if (quadrant === Quadrant.Two) {
      x = Math.random() * (maxX - minX) + minX;
      y = squareRoot(radius * radius - x * x);
      x = 101.4 - x;
      y = 101.4 - y;
    }

    if (quadrant === Quadrant.Three) {
      x = Math.random() * (maxX - minX) + minX;
      y = squareRoot(radius * radius - x * x);
      x = 101 - x;
    }

    return {
      x: x - correction,
      y: y - correction,
      quantity,
      quadrant,
      ring,
      info,
    };
  };

  const addPoint = (quadrant: Quadrant, point: Point) => {
    if (quadrant === 0) positionsQZero.push(point);
    else if (quadrant === 1) positionsQOne.push(point);
    else if (quadrant === 2) positionsQTwo.push(point);
    else if (quadrant === 3) positionsQThree.push(point);
  };

  const dataProcessing = (
    validQuadrants: { index: Quadrant; name: string }[],
    validRings: { index: Ring; name: string }[]
  ) => {
    const uniqueQuadrants = new Set();
    const quadrants: Group[] = [];

    // Get unique quadrants
    for (const object of data) {
      uniqueQuadrants.add(object.quadrant);
    }

    // Group by Quadrant
    if (uniqueQuadrants.size > 0) {
      uniqueQuadrants.forEach((quadrant: any) => {
        quadrants.push({
          description: quadrant,
          elements: data.filter(
            (elem: { quadrant: any }) => elem.quadrant === quadrant
          ),
        });
      });

      // Group by Rings
      quadrants.forEach((quadrant) => {
        const uniqueRings = new Set();
        const rings: Group[] = [];

        // Get unique rings
        for (const object of quadrant.elements) {
          uniqueRings.add(object.ring);
        }

        if (uniqueRings.size > 0) {
          uniqueRings.forEach((ring: any) => {
            rings.push({
              description: ring,
              elements: quadrant.elements.filter(
                (quadrant) => quadrant.ring === ring
              ),
            });
          });

          // Group by Topics
          rings.forEach((ring) => {
            const uniqueTopics = new Set();
            const topics: Group[] = [];

            // Get unique topics
            for (const object of ring.elements) {
              uniqueTopics.add(object.description);
            }

            if (uniqueTopics.size > 0) {
              uniqueTopics.forEach((topic: any) => {
                topics.push({
                  description: topic,
                  elements: ring.elements.filter(
                    (ring) => ring.description === topic
                  ),
                });
              });

              // Create Points
              topics.forEach((topic) => {
                const quadIndex = getQuadrant(
                  quadrant.description,
                  validQuadrants
                );
                const ringIndex = getRing(ring.description, validRings);
                const newPoint = randomPosition(
                  quadIndex,
                  ringIndex,
                  topic.elements.length,
                  topic.elements
                );

                addPoint(quadIndex, newPoint);
              });
            }
          });
        }
      });

      setPositionsQZero(positionsQZero);
      setPositionsQOne(positionsQOne);
      setPositionsQTwo(positionsQTwo);
      setPositionsQThree(positionsQThree);
    }
  };

  const getQuadrant = (
    quadrantName: string,
    validQuadrants: { index: Quadrant; name: string }[]
  ): number => {
    if (!quadrantName) return 0;
    const found = validQuadrants?.find(
      (quadrant) => quadrant.name === t(quadrantName)
    );
    return found ? found.index : -1;
  };

  const getRing = (
    ringName: string,
    validRings: { index: Ring; name: string }[]
  ): number => {
    const found = validRings?.find(
      (ring) => ring?.name?.toUpperCase() === ringName?.toUpperCase()
    );
    return found ? found.index : -1;
  };

  const generateValidQuadrants = () => {
    const { quadrants } = props;
    const validQuadrants: { index: Quadrant; name: string }[] = [];

    if (quadrants) {
      if (quadrants.length === 4) {
        validQuadrants.push({ index: Quadrant.Zero, name: quadrants[0] });
        validQuadrants.push({ index: Quadrant.One, name: quadrants[1] });
        validQuadrants.push({ index: Quadrant.Two, name: quadrants[2] });
        validQuadrants.push({
          index: Quadrant.Three,
          name: quadrants[3],
        });
      } else {
        throw new Error("You need four quadrants...");
      }
    } else {
      throw new Error("Your quadrants are not valid...");
    }

    setValidQuadrants(validQuadrants);
    generateValidRings(validQuadrants);
  };

  const generateValidRings = (
    validQuadrants: { index: Quadrant; name: string }[]
  ) => {
    const validRings: { index: Ring; name: string }[] = [];

    if (rings) {
      if (rings.length === 4) {
        validRings.push({ index: Ring.Zero, name: rings[0] });
        validRings.push({ index: Ring.One, name: rings[1] });
        validRings.push({ index: Ring.Two, name: rings[2] });
        validRings.push({ index: Ring.Three, name: rings[3] });
      } else {
        throw new Error("You need four rings...");
      }
    } else {
      throw new Error("Your rings are not valid...");
    }

    setValidRings(validRings);
    dataProcessing(validQuadrants, validRings);
  };

  const onClickElement = (data: any) => {
    onClick(data);
  };

  useEffect(() => {
    setPositionsQZero([]);
    setPositionsQOne([]);
    setPositionsQTwo([]);
    setPositionsQThree([]);
    generateValidQuadrants();
  }, []);

  return (
    <>
      <div
        style={{
          width,
          aspectRatio: "auto 1/1",
        }}
        className={
          theme === "dark" ? "background-quadrant-dark" : "background-quadrant"
        }
      >
        <div style={{ width: "100%", display: "flex" }}>
          <div
            className={
              theme === "dark" ? "label-dark label-q3" : "label label-q3"
            }
          >
            <div>{quadrants[3]}</div>
          </div>
          <div
            className={
              theme === "dark" ? "label-dark label-q0" : "label label-q0"
            }
          >
            <div>{quadrants[0]}</div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "row" }}>
          <div
            className={
              theme === "dark"
                ? "quadrant-3-ring-3-dark box-dark"
                : "quadrant-3-ring-3 box"
            }
          >
            <DrawPoints
              points={positionsQThree}
              onClick={onClickElement}
              quadrant={Quadrant.Three}
            />

            <div
              className={
                theme === "dark"
                  ? "quadrant-3-ring-2-dark box-dark"
                  : "quadrant-3-ring-2 box"
              }
            >
              <div
                className={
                  theme === "dark"
                    ? "quadrant-3-ring-1-dark box-dark"
                    : "quadrant-3-ring-1 box"
                }
              >
                <div
                  className={
                    theme === "dark"
                      ? "quadrant-3-ring-0-dark box-dark"
                      : "quadrant-3-ring-0 box"
                  }
                />
              </div>
            </div>
          </div>

          <div
            className={
              theme === "dark"
                ? "quadrant-0-ring-3-dark box-dark"
                : "quadrant-0-ring-3 box"
            }
          >
            <DrawPoints
              points={positionsQZero}
              onClick={onClickElement}
              quadrant={Quadrant.Zero}
            />

            <div
              className={
                theme === "dark"
                  ? "quadrant-0-ring-2-dark box-dark"
                  : "quadrant-0-ring-2 box"
              }
            >
              <div
                className={
                  theme === "dark"
                    ? "quadrant-0-ring-1-dark box-dark"
                    : "quadrant-0-ring-1 box"
                }
              >
                <div
                  className={
                    theme === "dark"
                      ? "quadrant-0-ring-0-dark box-dark"
                      : "quadrant-0-ring-0 box"
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className={theme === "dark" ? "ring-labels-dark" : "ring-labels"}>
          <div className="r-label r-label-3 r-left">{rings[3]}</div>
          <div className="r-label r-label-2 r-left">{rings[2]}</div>
          <div className="r-label r-label-1 r-left">{rings[1]}</div>
          <div className="r-label r-label-0 r-left">{rings[0]}</div>

          <div className="r-label r-label-0 r-right">{rings[0]}</div>
          <div className="r-label r-label-1 r-right">{rings[1]}</div>
          <div className="r-label r-label-2 r-right">{rings[2]}</div>
          <div className="r-label r-label-3 r-right">{rings[3]}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "row" }}>
          <div
            className={
              theme === "dark"
                ? "quadrant-2-ring-3-dark box-dark"
                : "quadrant-2-ring-3 box"
            }
          >
            <DrawPoints
              points={positionsQTwo}
              onClick={onClickElement}
              quadrant={Quadrant.Two}
            />

            <div
              className={
                theme === "dark"
                  ? "quadrant-2-ring-2-dark box-dark"
                  : "quadrant-2-ring-2 box"
              }
            >
              <div
                className={
                  theme === "dark"
                    ? "quadrant-2-ring-1-dark box-dark"
                    : "quadrant-2-ring-1 box"
                }
              >
                <div
                  className={
                    theme === "dark"
                      ? "quadrant-2-ring-0-dark box-dark"
                      : "quadrant-2-ring-0 box"
                  }
                />
              </div>
            </div>
          </div>

          <div
            className={
              theme === "dark"
                ? "quadrant-1-ring-3-dark box-dark"
                : "quadrant-1-ring-3 box"
            }
          >
            <DrawPoints
              points={positionsQOne}
              onClick={onClickElement}
              quadrant={Quadrant.One}
            />

            <div
              className={
                theme === "dark"
                  ? "quadrant-1-ring-2-dark box-dark"
                  : "quadrant-1-ring-2 box"
              }
            >
              <div
                className={
                  theme === "dark"
                    ? "quadrant-1-ring-1-dark box-dark"
                    : "quadrant-1-ring-1 box"
                }
              >
                <div
                  className={
                    theme === "dark"
                      ? "quadrant-1-ring-0-dark box-dark"
                      : "quadrant-1-ring-0 box"
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ width: "100%", display: "flex" }}>
          <div
            className={
              theme === "dark" ? "label-dark label-q2" : "label-white label-q2"
            }
          >
            {quadrants[2]}
          </div>
          <div
            className={
              theme === "dark" ? "label-dark label-q1" : "label-white label-q1"
            }
          >
            {quadrants[1]}
          </div>
        </div>
      </div>
    </>
  );
};

export default Radar;
